import classNames from 'classnames';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import Collapse from '../../assets/icomoon/circle-down.svg?react';
import Open from '../../assets/icomoon/circle-up.svg?react';
import CopyLink from '../../assets/icomoon/link.svg?react';
import RepeatAll from '../../assets/icomoon/loop2.svg?react';
import Next from '../../assets/icomoon/next.svg?react';
import Previous from '../../assets/icomoon/previous.svg?react';
import Repeat from '../../assets/icomoon/spinner11.svg?react';
import soundCloudLogo from '../../assets/soundcloud.svg';
import type Hls from 'hls.js/dist/hls.js';
import formatDuration from '../../utilities/formatDuration';
import PlaylistItem from '../../models/playlistItem/playlistItem';
import RepeatState from '../../models/repeatState';
import Source from '../../models/source';
import { TrackLength } from '../../models/track';
import { soundCloudAccess } from '../../models/track/soundCloudTrack';
import { ActivePlaylistContext } from '../../services/activePlaylist/activePlaylist';
import playlistToShow from '../../services/activePlaylist/playlistToShow';
import { DropboxContext } from '../../services/dropbox/dropboxService';
import { GoogleApiContext, GoogleScope } from '../../services/gapi/gapiService';
import { LibraryContext } from '../../services/library/libraryService';
import * as OneDriveApi from '../../services/oneDrive/oneDriveApi';
import { OneDriveContext } from '../../services/oneDrive/oneDriveService';
import {
  loadNowPlaying,
  NowPlaying,
  resumeIndex,
  resumePositionMs,
  usesAudioElement,
} from '../../services/playback/nowPlaying';
import { ScreenDetectionContext, ScreenSize } from '../../services/screenDetection/screenDetection';
import useBlurWhenHidden from '../../services/shortcuts/useBlurWhenHidden';
import {
  SmallDeviceContext,
  SmallDeviceShow,
} from '../../services/smallDevices/smallDeviceService';
import { SoundCloudApiError, SoundCloudStream } from '../../services/soundCloud/soundCloudApi';
import { SoundCloudContext } from '../../services/soundCloud/soundCloudService';
import { SpotifyContext } from '../../services/spotify/spotifyService';
import { Context } from '../../services/_globalContext/context';
import Constants, { Keys, messages } from '../../settings';
import ErrorBoundary from '../common/errorBoundary/errorBoundary';
import { soundCloudPermalink } from '../soundCloud/openOnSoundCloud';
import MixcloudPlayerWrapper, {
  MixcloudPlayerHandle,
  MixcloudPlayRequest,
} from '../mixcloud/mixcloudPlayerWrapper';
import SpotifyPlayerWrapper from '../spotify/spotifyPlayerWrapper';
import s from './player.module.scss';
import { seekTarget } from './seek';
import useGoToCurrentTrackShortcut from './useGoToCurrentTrackShortcut';
import useMediaSession from './useMediaSession';
import useNowPlayingPosition from './useNowPlayingPosition';
import usePlayerShortcuts from './usePlayerShortcuts';
import usePlayPauseShortcut from './usePlayPauseShortcut';
import usePlaybackFailures from './usePlaybackFailures';

interface Props {}

export enum PlayerSize {
  Hide = 'hidePlayer',
  Small = 'small',
  Medium = 'medium',
  // Large = 'large',
}

export default function Player({}: Props) {
  const ctx = useContext(Context);
  const screenSize = useContext(ScreenDetectionContext);
  const smallDevSvc = useContext(SmallDeviceContext);
  const libCtx = useContext(LibraryContext);
  const {
    currentTrack,
    next,
    prev,
    setActivePlaylist,
    setCurrentTrack,
    currentPlaylistIndex,
    setCurrentPlaylistItem,
    value: activePlaylistValue,
    sourcePlaylistId,
    requestLocate,
  } = useContext(ActivePlaylistContext);
  const navigate = useNavigate();
  const gapiCtx = useContext(GoogleApiContext);
  const oneDriveCtx = useContext(OneDriveContext);
  const dbxCtx = useContext(DropboxContext);
  const spotifyCtx = useContext(SpotifyContext);
  const soundCloudCtx = useContext(SoundCloudContext);

  /**
   * Take care when using the track object.
   * For the spotify and youtube components, it is possible to play tracks that are not
   * in the users library.
   *
   * In these cases getLibraryTrack returns NullTrack.
   * The Player handles this gracefully
   * and instead uses currentTrack when possible and still plays the track.
   */
  const track = libCtx.getLibraryTrack(currentTrack);

  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;

  const gapiCtxRef = useRef(gapiCtx);
  gapiCtxRef.current = gapiCtx;

  const currentTrackRef = useRef(currentTrack);
  currentTrackRef.current = currentTrack;

  /** The last track we auto-retried, so one failure never becomes a loop. */
  const retriedTrackIdRef = useRef<string>(undefined);

  /**
   * Every failed track goes through here, so that something can count them.
   * See usePlaybackFailures for why that matters.
   */
  const { failPlayback, notePlaybackStarted } = usePlaybackFailures({
    setTrackMissing: libCtx.setTrackMissing,
    advance: next,
    report: ctx.callSnackbar,
  });

  /**
   * A failure that, taken on its own, means this track is bad.
   *
   * It no longer writes the missing flag itself. Whether that belief survives
   * depends on what happens next: one isolated failure is the track's fault,
   * three in a row are not, and usePlaybackFailures is what knows the
   * difference. It also decides whether to skip onwards or stop.
   */
  const markTrackMissingOnError = useCallback(
    function markTrackMissingOnError(reason?: string) {
      failPlayback({ track, trackFault: true, reason });
    },
    [failPlayback, track],
  );

  /**
   * A failure that says nothing about the track - the network, the provider,
   * the token, or the player's own configuration. Never flags anything.
   */
  const skipTrackOnError = useCallback(
    function skipTrackOnError(reason?: string) {
      failPlayback({ track, trackFault: false, reason });
    },
    [failPlayback, track],
  );
  /**
   * Decide whether a failed Google Drive track is genuinely gone before
   * writing anything into the library.
   *
   * The audio element cannot report an HTTP status: a 403 and a 404 both
   * arrive as MEDIA_ERR_SRC_NOT_SUPPORTED, because in both cases what came
   * back was a JSON error body rather than audio. Guessing between them is how
   * good tracks kept getting flagged missing - first from a scope the user had
   * not granted, then from a freshly issued token that had not yet reached the
   * service worker. Both are indistinguishable from a deleted file by shape,
   * and neither is one.
   *
   * So ask Drive. One byte is enough to learn the status and does not pull
   * the track.
   */
  const classifyDriveFailure = useCallback(
    async function classifyDriveFailure() {
      try {
        const url = await gapiCtxRef.current.getDriveFileUrl(
          currentTrackRef.current.id,
          undefined,
          true,
        );

        const res = await fetch(url, { headers: { Range: 'bytes=0-0' } });

        if (res.status === 404) {
          // Only "missing" if the token could have seen the file in the first
          // place. Drive returns 404 rather than 403 for anything the caller is
          // not permitted to read, so an under-scoped token makes every track in
          // the library look deleted - which is precisely how good tracks got
          // flagged before.
          if (gapiCtxRef.current.canReadDriveFiles) {
            markTrackMissingOnError('drive:404');
          } else {
            ctxRef.current.callSnackbar(messages(Keys.driveFilesPermissionMissing));
          }

          return;
        }

        if (res.status === 401 || res.status === 403) {
          ctxRef.current.callSnackbar(
            gapiCtxRef.current.canReadDriveFiles
              ? 'Google Drive refused this track. The sign-in may have just been renewed - ' +
                  'press play again.'
              : messages(Keys.driveFilesPermissionMissing),
          );
          return;
        }

        if (res.ok) {
          // Reachable now, so whatever stopped the element was transient. Asking
          // the user to press play again is just making them do by hand what we
          // already know will work, so do it once ourselves.
          //
          // Once per track, tracked by id: if the second attempt fails too then
          // something is actually wrong and looping on it would only hide that.
          if (retriedTrackIdRef.current !== currentTrackRef.current.id) {
            retriedTrackIdRef.current = currentTrackRef.current.id;

            const el = audioEl.current;

            if (el) {
              el.src = url;
              const retryPromise = el.play();

              if (retryPromise) {
                retryPromise.catch(function () {
                  ctxRef.current.callSnackbar(
                    'That track would not start. Press play to try again.',
                  );
                });
              }
            }

            return;
          }

          ctxRef.current.callSnackbar('That track would not start. Press play to try again.');
          return;
        }

        ctxRef.current.callSnackbar(`Google Drive returned ${res.status} for this track.`);
      } catch (err) {
        // Could not even ask. Still not grounds for calling the track missing.
        ctxRef.current.callSnackbar(err);
      }
    },
    [markTrackMissingOnError],
  );

  const resetTrackMissing = useCallback(
    function resetTrackMissing() {
      if (track) {
        libCtx.setTrackMissing(track, false);
      }
    },
    [libCtx, track],
  );

  const audioEl = useRef<HTMLAudioElement>(null);

  /**
   * The hls.js session for a SoundCloud track on a browser without native
   * HLS. Destroyed whenever the track changes, so a half-loaded session
   * never bleeds into the next track's <audio> - see docs/SOUNDCLOUD.md
   * section 3.
   */
  const hlsRef = useRef<Hls>(undefined);

  /**
   * Read through a ref, not the closure: the SoundCloud branch may sign in
   * first and play afterwards, and by then the context the play effect
   * closed over is the signed-out one.
   */
  const soundCloudCtxRef = useRef(soundCloudCtx);
  soundCloudCtxRef.current = soundCloudCtx;

  const [ytIFrameSrcUrl, setYtIFrameSrcUrl] = useState<string | undefined>(undefined);

  /**
   * The show the Mixcloud widget should be playing, or nothing while
   * another source has the floor. A new object per play, so that choosing
   * the show already loaded starts it again rather than changing nothing.
   */
  const [mixcloudRequest, setMixcloudRequest] = useState<MixcloudPlayRequest>();

  /**
   * The Mixcloud widget, for the one thing that has to be asked of it from
   * out here. Its transport is Mixcloud's own bar, not the player's.
   */
  const mixcloudPlayer = useRef<MixcloudPlayerHandle>(null);

  /**
   * Whether the YouTube player, once it exists, should start on its own.
   * False for exactly one video per session: the one being restored.
   */
  const ytAutoplayRef = useRef(true);

  /**
   * Where the last session left off, until it is either used or given up on.
   *
   * Read at the first render and never again: the recorder below starts
   * overwriting it the moment anything plays, so by the time the queue has
   * loaded and a provider is ready, this ref is the only copy left.
   */
  const pendingResumeRef = useRef<NowPlaying | undefined>(loadNowPlaying());
  /** Whether the one restore a session gets has already been settled. */
  const didRestoreRef = useRef(false);
  /**
   * The track the restore put back, until the play effect has seen it.
   *
   * Kept apart from the saved position because the two answer different
   * questions: this one is "did anybody press play", and the answer is no
   * however far into the track the last session got. A track left three
   * seconds in has nothing worth seeking to and still must not start on its
   * own, which is what asking the position alone used to conclude.
   */
  const restoredItemRef = useRef<PlaylistItem>(undefined);

  const { recordPosition } = useNowPlayingPosition({
    currentTrack,
    playlistIndex: currentPlaylistIndex,
    audioEl,
    ytPlayer: gapiCtx.ytPlayer,
    spotifyPlayer: spotifyCtx.player,
  });

  /**
   * Whether a track of this kind could be played *right now*, without asking
   * anybody to sign in to anything.
   *
   * Restoring must never be the thing that opens an OAuth window: the user
   * opened the app, they did not ask for Drive. So a restore waits for its
   * provider, and where that provider never arrives - not signed in this
   * session - nothing is restored and nothing is said about it either.
   */
  const providerReady = useCallback(
    function providerReady(trackType: Source) {
      switch (trackType) {
        case Source.YouTube:
          return gapiCtx.iframeApiLoaded;

        case Source.GoogleDrive:
          return gapiCtx.gapiAuthorized && gapiCtx.canReadDriveFiles;

        case Source.OneDrive:
          return oneDriveCtx.isAuthenticated;

        case Source.Dropbox:
          return !!dbxCtx.account;

        case Source.Spotify:
          return !!spotifyCtx.accessToken && !!spotifyCtx.playerId;

        case Source.SoundCloud:
          return !!soundCloudCtx.accessToken;

        // Neither needs an account: a Mixcloud key is public and a podcast
        // track carries its own url.
        case Source.Mixcloud:
        case Source.Podcast:
          return true;

        default:
          return false;
      }
    },
    [gapiCtx, oneDriveCtx, dbxCtx, spotifyCtx, soundCloudCtx],
  );

  useEffect(
    function resumeLastSession() {
      const pending = pendingResumeRef.current;

      if (!pending || didRestoreRef.current) {
        return;
      }

      // The player reads the *library* record for the track it is given - the
      // podcast branch plays its id, everything reads its title - and the
      // library arrives a moment after the app does. Restoring into an empty
      // library gets NullTrack and plays nothing at all.
      if (!libCtx.libraryLoaded) {
        return;
      }

      if (currentTrack.trackType !== Source.Null) {
        // The user got here first. Being dragged back to what a previous
        // session was playing would be worse than forgetting it.
        didRestoreRef.current = true;
        pendingResumeRef.current = undefined;

        return;
      }

      const index = resumeIndex(activePlaylistValue.tracks, pending);

      // Not yet, or not ever: the queue is read out of localStorage a moment
      // after mount, providers sign in later still, and the track may have
      // been taken out of the queue since. Each of those simply tries again on
      // the next render, and a provider that never signs in never resumes.
      if (index < 0 || !providerReady(pending.trackType)) {
        return;
      }

      didRestoreRef.current = true;
      restoredItemRef.current = activePlaylistValue.tracks[index];

      setCurrentPlaylistItem(index);
      // The queue's own object rather than a copy: updateActivePlaylist finds
      // the current item by identity, and so does the play effect when it asks
      // whether this track is the restored one.
      setCurrentTrack(activePlaylistValue.tracks[index]);
    },
    [
      activePlaylistValue,
      currentTrack,
      libCtx.libraryLoaded,
      providerReady,
      setCurrentPlaylistItem,
      setCurrentTrack,
    ],
  );

  /**
   * Play if paused, pause if playing - whichever of the three surfaces holds
   * the current track. False when there is nothing to toggle, which is what
   * leaves space to the page.
   *
   * Deliberately not routed through the play effect: that effect *loads* a
   * track, and re-running it to resume would fetch a fresh url and start the
   * track again from the top.
   */
  const togglePlayPause = useCallback(
    function togglePlayPause() {
      switch (currentTrack.trackType) {
        case Source.Null:
          return false;

        case Source.YouTube: {
          const player = gapiCtx.ytPlayer.current;

          // The ref outlives the player it was set from, and a player that has
          // not finished loading has none of these methods yet.
          if (!player || typeof player.getPlayerState !== 'function') {
            return false;
          }

          if (player.getPlayerState() === YT.PlayerState.PLAYING) {
            player.pauseVideo();
          } else {
            player.playVideo();
          }

          return true;
        }

        case Source.Spotify: {
          if (!spotifyCtx.player) {
            return false;
          }

          spotifyCtx.player.togglePlay().catch(console.debug);

          return true;
        }

        case Source.Mixcloud: {
          if (!mixcloudPlayer.current) {
            return false;
          }

          mixcloudPlayer.current.togglePlay();

          return true;
        }

        default: {
          const el = audioEl.current;

          // `src` is empty until something has been loaded into the element,
          // and calling play() on that is a rejected promise and no audio.
          if (!el || !el.src || !usesAudioElement(currentTrack.trackType)) {
            return false;
          }

          if (el.paused) {
            const playPromise = el.play();

            if (playPromise) {
              playPromise.catch(console.debug);
            }
          } else {
            el.pause();
          }

          return true;
        }
      }
    },
    [currentTrack, gapiCtx.ytPlayer, spotifyCtx.player],
  );

  usePlayPauseShortcut(togglePlayPause);

  /**
   * ctrl+L: back to the track that is playing, in the playlist it was
   * started from. playlistToShow picks the playlist; a request in the
   * active-playlist context carries it to the library page and the
   * playlist view, which switch, select and scroll.
   *
   * Navigation only when the library page is not up. When it is, it moves
   * the url itself with pushState as it switches playlists, and a router
   * push on top of that would leave two history entries for one keystroke.
   */
  const goToCurrentTrack = useCallback(
    function goToCurrentTrack() {
      const playlistId = playlistToShow(
        currentTrack,
        sourcePlaylistId,
        activePlaylistValue,
        libCtx.libraryPlaylist,
        libCtx.playlists,
      );

      if (playlistId === undefined) {
        return false;
      }

      requestLocate(playlistId);

      if (!window.location.pathname.startsWith('/app/library')) {
        navigate(`/app/library/${playlistId}`);
      }

      return true;
    },
    [
      currentTrack,
      sourcePlaylistId,
      activePlaylistValue,
      libCtx.libraryPlaylist,
      libCtx.playlists,
      requestLocate,
      navigate,
    ],
  );

  useGoToCurrentTrackShortcut(goToCurrentTrack);

  /**
   * Moves the playhead by `seconds` on whichever surface holds the track,
   * clamped so a skip never runs off the end (seekTarget). False when no
   * surface can: nothing loaded, or a YouTube video that has never played -
   * seekTo on that *starts* it, the same trap the restore path steps
   * around.
   */
  const seekBy = useCallback(
    function seekBy(seconds: number) {
      switch (currentTrack.trackType) {
        case Source.Null:
          return false;

        case Source.YouTube: {
          const player = gapiCtx.ytPlayer.current;

          if (!player || typeof player.getPlayerState !== 'function') {
            return false;
          }

          const state = player.getPlayerState();

          if (state !== YT.PlayerState.PLAYING && state !== YT.PlayerState.PAUSED) {
            return false;
          }

          player.seekTo(seekTarget(player.getCurrentTime(), seconds, player.getDuration()), true);

          return true;
        }

        case Source.Spotify: {
          const player = spotifyCtx.player;

          if (!player) {
            return false;
          }

          player
            .getCurrentState()
            .then((state) => {
              if (state) {
                return player.seek(
                  seekTarget(state.position / 1000, seconds, state.duration / 1000) * 1000,
                );
              }
            })
            .catch(console.debug);

          return true;
        }

        case Source.Mixcloud: {
          if (!mixcloudPlayer.current) {
            return false;
          }

          mixcloudPlayer.current.seekBy(seconds);

          return true;
        }

        default: {
          const el = audioEl.current;

          if (!el || !el.src || !usesAudioElement(currentTrack.trackType)) {
            return false;
          }

          el.currentTime = seekTarget(el.currentTime, seconds, el.duration);

          return true;
        }
      }
    },
    [currentTrack, gapiCtx.ytPlayer, spotifyCtx.player],
  );

  usePlayerShortcuts({
    hasTrack: currentTrack.trackType !== Source.Null,
    next: () => {
      next();
    },
    prev: () => {
      prev();
    },
    seekBy,
    toggleRepeat: ctx.toggleRepeat,
  });

  /**
   * The embeds are hidden with display: none, which does not take the focus
   * off them - and a focused iframe, hidden or not, keeps every key from the
   * page and so from the shortcuts. Blurred when hidden, then; the Mixcloud
   * frame does the same for itself from its `hidden` prop.
   */
  const youTubeEmbedHidden =
    currentTrack.trackType !== Source.YouTube || ctx.playerSize === PlayerSize.Small;

  useBlurWhenHidden(gapiCtx.playerEl, youTubeEmbedHidden);

  /**
   * The media keys and the OS's media controls. `play` and `pause` mean
   * exactly that for the audio element, where the OS can see the state; for
   * an embed, which runs its own session, they fall back to the toggle.
   */
  useMediaSession({
    enabled: currentTrack.trackType !== Source.Null,
    title: track.type !== Source.Null && track.title ? track.title : 'Fadila',
    artist: track.artist,
    album: track.album,
    actions: {
      play: () => {
        const el = audioEl.current;

        if (el && el.src && usesAudioElement(currentTrack.trackType)) {
          if (el.paused) {
            const playPromise = el.play();

            if (playPromise) {
              playPromise.catch(console.debug);
            }
          }

          return;
        }

        togglePlayPause();
      },
      pause: () => {
        const el = audioEl.current;

        if (el && el.src && usesAudioElement(currentTrack.trackType)) {
          if (!el.paused) {
            el.pause();
          }

          return;
        }

        togglePlayPause();
      },
      next: () => {
        next();
      },
      prev: () => {
        prev();
      },
      seekBy,
    },
  });

  /**
   * The position this track should start at, in seconds - and never twice.
   *
   * The saved playhead belongs to one track and one moment: the first time
   * this session plays the track it was left on. Anything else starting
   * spends it, so playing that track again later starts where every other
   * track starts rather than dropping the user back at the point they once
   * gave up.
   */
  const takeResumeSeconds = useCallback(function takeResumeSeconds(item: PlaylistItem) {
    const pending = pendingResumeRef.current;

    if (!pending || item.trackType === Source.Null) {
      return 0;
    }

    pendingResumeRef.current = undefined;

    return resumePositionMs(pending, item) / 1000;
  }, []);

  useEffect(
    function setAudioElEventListener() {
      const curAudioEl = audioEl.current;
      if (curAudioEl) {
        curAudioEl.addEventListener('ended', next);
        // Audio is coming out. Whatever failed before this was not systemic,
        // so the consecutive-failure run ends here. This is the event rather
        // than "we obtained a url", because a url that resolves and then will
        // not play is exactly the case the counter exists to catch.
        curAudioEl.addEventListener('playing', notePlaybackStarted);
      }

      return function cleanup() {
        if (curAudioEl) {
          curAudioEl.removeEventListener('ended', next);
          curAudioEl.removeEventListener('playing', notePlaybackStarted);
        }
      };
    },
    [audioEl, next, notePlaybackStarted],
  );

  useEffect(
    function listenForAudioElementErrors() {
      /**
       * https://developer.mozilla.org/en-US/docs/Web/API/MediaError
       * @param ev
       */
      function audioElErrorListener(ev: ErrorEvent) {
        if (audioEl.current!.error) {
          switch (audioEl.current!.error.code) {
            case audioEl.current!.error.MEDIA_ERR_ABORTED: // 1
              console.debug('MEDIA_ERR_ABORTED', audioEl.current!.error, ev);
              break;
            case audioEl.current!.error.MEDIA_ERR_NETWORK: // 2
              console.debug(
                'MEDIA_ERR_NETWORK - likely an expired OneDrive temporary link. Is handled elsewhere.',
                audioEl.current!.error,
                ev,
              );
              break;
            case audioEl.current!.error.MEDIA_ERR_DECODE: // 3
              markTrackMissingOnError('MEDIA_ERR_DECODE');
              console.debug('MEDIA_ERR_DECODE - unsupported track', audioEl.current!.error, ev);
              break;
            case audioEl.current!.error.MEDIA_ERR_SRC_NOT_SUPPORTED: // 4
              // Drive cannot be judged from this event alone - see
              // classifyDriveFailure. Every other source still behaves as before,
              // where an unsupported source really does mean a bad file.
              if (
                currentTrackRef.current &&
                currentTrackRef.current.trackType === Source.GoogleDrive
              ) {
                classifyDriveFailure();
              } else {
                // Still the best available guess for a single track, but no
                // longer believed on its own: an outage at OneDrive or Dropbox
                // returns an error body rather than audio and lands here too,
                // which is the same trap the Drive branch above documents. A
                // second failure behind this one takes the flag back.
                markTrackMissingOnError('MEDIA_ERR_SRC_NOT_SUPPORTED');
                console.debug(
                  'MEDIA_ERR_SRC_NOT_SUPPORTED - possible 404, skipping track',
                  audioEl.current!.error,
                  ev,
                );
              }
              break;
          }
        }
      }

      const curAudioEl = audioEl.current;
      if (curAudioEl) {
        curAudioEl.addEventListener('error', audioElErrorListener);
      }

      return function cleanup() {
        if (curAudioEl) {
          curAudioEl.removeEventListener('error', audioElErrorListener);
        }
      };
    },
    [next, markTrackMissingOnError, classifyDriveFailure],
  );

  useEffect(
    function play() {
      const curAudioEl = audioEl.current;
      let halt = false;
      // Stays undefined until a source actually starts playback - the checks
      // below rely on that, so the type has to admit it.
      let playPromise: Promise<void> | undefined;

      /**
       * Handles errors thrown by the Audio element play() promise
       * DOMException's are usually when user navigates too quickly
       * @param err
       */
      function catchAudioElementPlayErrors(err: any) {
        if (!(err instanceof DOMException)) {
          ctx.callSnackbar(err);
          return;
        }

        // Not every DOMException is the harmless one. AbortError means a track
        // change interrupted play() and is worth nothing more than a debug line.
        // NotAllowedError means the browser refused to start playback without a
        // user gesture - routine on iOS - and lumping the two together is why
        // "tap play, nothing happens, no error" was the symptom on a phone,
        // console.debug being noop in production.
        if (err.name === 'NotAllowedError') {
          ctx.callSnackbar(
            'This device would not start playback on its own. Press play to start the track.',
          );
          return;
        }

        console.debug(err, 'Playback likely aborted due to track change');
      }

      async function handleOneDriveExpired() {
        if (
          curAudioEl &&
          curAudioEl.error &&
          curAudioEl.error.code === curAudioEl.error.MEDIA_ERR_NETWORK
        ) {
          const trackWithFreshUrl = await OneDriveApi.getItem(
            oneDriveCtx.graphClient,
            ctx.callSnackbar,
          )(currentTrack.id);

          if (curAudioEl && halt === false) {
            // FIX: If i start working with MediaStreams
            // I should be able to get a batch of data first
            // and then switch sources when ready
            const curTime = curAudioEl.currentTime;
            curAudioEl.src = (trackWithFreshUrl as any)['@microsoft.graph.downloadUrl'];
            curAudioEl.currentTime = curTime;

            playPromise = curAudioEl.play();
            if (playPromise) {
              playPromise.catch(catchAudioElementPlayErrors);
            }
          }
        }
      }
      async function handleGapiExpired() {
        if (
          curAudioEl &&
          curAudioEl.error &&
          curAudioEl.error.code === curAudioEl.error.MEDIA_ERR_NETWORK
        ) {
          const fileUrl = await gapiCtx.getDriveFileUrl(currentTrack.id, undefined, true);

          if (curAudioEl && halt === false) {
            const curTime = curAudioEl.currentTime;
            curAudioEl.src = fileUrl;
            curAudioEl.currentTime = curTime;

            playPromise = curAudioEl.play();
            if (playPromise) {
              playPromise.catch(catchAudioElementPlayErrors);
            }
          }
        }
      }

      function handleSpotifyStateChange(state: Spotify.PlaybackState) {
        // Spotify plays through the Web Playback SDK rather than the audio
        // element, so it needs its own "actually started" signal.
        if (state && !state.paused) {
          notePlaybackStarted();

          if (awaitingResumePause) {
            // The SDK has no way to cue a track: putting one on the device
            // means playing it. So a restored track is started at the saved
            // position and stopped again at the first sign of life. A moment
            // of audio is the price of not losing the position altogether.
            awaitingResumePause = false;

            if (spotifyCtx.player) {
              spotifyCtx.player.pause().catch(console.debug);
            }
          }
        }

        if (
          currentTrack.trackType === Source.Spotify &&
          state &&
          state.duration - state.position < 1000 &&
          state.paused
        ) {
          next();
        }
      }

      function doSetPlayerSizeSmall() {
        if (
          ctx.playerSize !== PlayerSize.Small &&
          screenSize !== ScreenSize.Small &&
          !ctx.settings.lockPlayerSizeMedium
        ) {
          ctx.setPlayerSize(PlayerSize.Small);
        }
      }

      /**
       * Whether this run of the effect is the last session coming back rather
       * than somebody pressing play. Nothing starts on its own when it is.
       */
      const isRestore = restoredItemRef.current === currentTrack;

      if (isRestore) {
        restoredItemRef.current = undefined;
      }

      /**
       * Where this track should start, when it is the one the last session was
       * left on. Zero for everything else, which is everything after the first
       * track a session plays - and zero as well for a track that was left at
       * its very beginning or played almost to the end.
       */
      const resumeSeconds = takeResumeSeconds(currentTrack);

      /** See handleSpotifyStateChange: the SDK cannot cue, only play. */
      let awaitingResumePause = isRestore;

      /**
       * Starts the audio element - or, on a restored track, parks it at the
       * saved position without playing.
       *
       * A restore is nobody's gesture, and browsers refuse audio nobody asked
       * for; reporting that refusal would be an error message for something
       * the user never did. So the track is loaded, cued, and one press from
       * carrying on.
       */
      function startAudioEl(el: HTMLAudioElement) {
        if (isRestore) {
          if (resumeSeconds > 0) {
            cueAudioEl(el, resumeSeconds);
          }

          return;
        }

        playPromise = el.play();

        if (playPromise) {
          playPromise.catch(catchAudioElementPlayErrors);
        }
      }

      /**
       * currentTime cannot be set before the browser knows how long the track
       * is, and assigning src does not make that so - metadata is an event
       * later. Setting it early is silently ignored, which is the kind of
       * failure that looks like the position was never saved.
       */
      function cueAudioEl(el: HTMLAudioElement, seconds: number) {
        if (el.readyState >= 1 /* HAVE_METADATA */) {
          el.currentTime = seconds;

          return;
        }

        el.addEventListener(
          'loadedmetadata',
          function seekToSaved() {
            if (!halt) {
              el.currentTime = seconds;
            }
          },
          { once: true },
        );
      }

      document.title = `Fadila${
        track.type !== Source.Null && track.title ? ` - ${track.title}` : ''
      }`;

      if (currentTrack) {
        switch (currentTrack.trackType) {
          case Source.YouTube:
            // `origin` is what the IFrame API validates its postMessage traffic
            // against, and YouTube expects an embed using enablejsapi to name
            // it. See the referrerPolicy on the iframe itself for the other
            // half of identifying ourselves - error 153 is what happens when
            // neither is in place.
            // `start` is how a restored video is cued. The IFrame API's
            // seekTo would do it too, but seekTo on a player that has never
            // played *starts* it, which is the one thing a restore must not
            // do; the url is read before the player can play anything.
            setYtIFrameSrcUrl(
              `https://www.youtube.com/embed/${currentTrack.id}` +
                `?enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}` +
                (resumeSeconds > 0 ? `&start=${Math.floor(resumeSeconds)}` : ''),
            );

            ytAutoplayRef.current = !isRestore;

            if (screenSize === ScreenSize.Small && !isRestore) {
              // Opening the app is not a request to be dropped into the
              // full-screen player.
              smallDevSvc.setState(SmallDeviceShow.Player);
            } else if (ctx.playerSize !== PlayerSize.Medium) {
              ctx.setPlayerSize(PlayerSize.Medium);
            }

            // The YouTube play effect then picks up the changed src url
            // This way the YT.Player is only created after the react re-render with updated src
            break;

          case Source.OneDrive:
            OneDriveApi.getItem(
              oneDriveCtx.graphClient,
              ctx.callSnackbar,
            )(currentTrack.id).then((driveItem) => {
              resetTrackMissing();

              // If api call was successful and context state is not authenticated
              // user must have been prompted to authenticate in relation to this call.
              // Authorization in this way outside the normal flow needs to be acknowledged
              // by the oneDrive context.
              if (!oneDriveCtx.isAuthenticated) {
                oneDriveCtx.setIsAuthenticated();
              }

              if (curAudioEl && halt === false) {
                curAudioEl.src = (driveItem as any)[Constants.MsGraphDlUrlProp];
                startAudioEl(curAudioEl);

                doSetPlayerSizeSmall();

                curAudioEl.addEventListener('error', handleOneDriveExpired);

                // Reset the graph downloadUrl
                // This was needed since the msgraph dl url has a maximum lifetime of circa 1h

                // We might wanna listen after an error and only act on that instead of this
                // -- update, we now do

                // I could try setting source to same broken url and testing event listeners
                // ..targetting that
                // Or not, we can listen to onerror on audioEl in the above case
                // however the event has no useful information about the error
                // this event listener would therefore apply to media of all types
                // and is probably not the best solution for this app
                // interval.current = setInterval(async () => {
                //   const isPaused = curAudioEl.paused;

                //   OneDriveApi.getItem
                //     (oneDriveCtx.graphClient)
                //     (ctx.callSnackbar)
                //     (track.id!).then(trackWithFreshUrl => {

                //       if (halt === false) {
                //         // FIX: When i start working with MediaStreams
                //         // I should be able to get a batch of data first
                //         // and then switch sources when ready
                //         const curTime = curAudioEl.currentTime;
                //         curAudioEl.src = (trackWithFreshUrl as any)['@microsoft.graph.downloadUrl'];
                //         curAudioEl.currentTime = curTime;

                //         if (isPaused === false) {
                //           curAudioEl.play();
                //         }
                //       }
                //     });
                // }, OneDriveApi.OneDriveDownloadUrlTimeoutMs);
              }
            }, markTrackMissingOnError);
            break;

          case Source.GoogleDrive:
            function playGDriveTrack() {
              gapiCtx.getDriveFileUrl(currentTrack.id, undefined, true).then((fileUrl) => {
                resetTrackMissing();

                if (curAudioEl && halt === false) {
                  curAudioEl.src = fileUrl;
                  startAudioEl(curAudioEl);

                  doSetPlayerSizeSmall();

                  curAudioEl.addEventListener('error', handleGapiExpired);
                }
              }, markTrackMissingOnError);
            }

            if (gapiCtx.gapiAuthorized && gapiCtx.canReadDriveFiles) {
              playGDriveTrack();
            } else if (gapiCtx.canAuthorizeInPlace) {
              // Where a popup works, ask for the missing permission and carry on
              // playing once it is granted. Asking for DriveReadonly explicitly
              // matters: a bare authorize() re-requests the scopes already held,
              // which for an appdata-only token grants nothing new and leaves
              // playback failing for the same reason as before.
              gapiCtx
                .authorize({ scope: GoogleScope.DriveReadonly })
                .then(playGDriveTrack, ctx.callSnackbar);
            } else {
              // Authorising here means a full-page redirect, and this effect
              // runs off a track change rather than a deliberate press - so say
              // what is needed instead of tearing the app down uninvited.
              ctx.callSnackbar(
                gapiCtx.gapiAuthorized
                  ? messages(Keys.driveFilesPermissionMissing)
                  : 'Not signed in to Google, so this track cannot be played. ' +
                      'Sign in from the setup screen.',
              );
            }
            break;

          case Source.Dropbox:
            function playDbxTrack() {
              dbxCtx.dbx.filesGetTemporaryLink({ path: currentTrack.id }).then((res) => {
                resetTrackMissing();

                if (curAudioEl && halt === false) {
                  curAudioEl.src = res.link;
                  startAudioEl(curAudioEl);

                  doSetPlayerSizeSmall();
                }
              }, markTrackMissingOnError);
            }

            if (!dbxCtx.account) {
              // Deliberately no allowRedirect here. On iOS authorising means
              // navigating the whole app to Dropbox, and being thrown out of the
              // app mid-listen is not a fix - the same call the Google branch
              // above makes for the same reason.
              if (dbxCtx.canAuthorizeInPlace) {
                dbxCtx.authorize().then(playDbxTrack, ctx.callSnackbar);
              } else {
                ctx.callSnackbar(
                  'Not signed in to Dropbox, so this track cannot be played. ' +
                    'Sign in from the setup screen.',
                );
              }
            } else {
              playDbxTrack();
            }

            break;

          case Source.Spotify:
            async function playSpotifyTrack() {
              if (spotifyCtx.player) {
                spotifyCtx.player.addListener('player_state_changed', handleSpotifyStateChange);
              }
              doSetPlayerSizeSmall();

              try {
                // We already checked for access token before calling playSpotifyTrack
                const result = await spotifyCtx.spotifyApi!.playTrack(
                  spotifyCtx.playerId!,
                  currentTrack.id,
                  resumeSeconds > 0 ? resumeSeconds * 1000 : undefined,
                );

                resetTrackMissing();

                return result;
              } catch (err) {
                markTrackMissingOnError();
              }
            }

            if (!spotifyCtx.accessToken) {
              // Deliberately no allowRedirect here. On iOS authorising means
              // navigating the whole app to Spotify, and being thrown out of
              // the app mid-listen is not a fix - the same call the Dropbox
              // branch above makes for the same reason.
              //
              // Signing in is not enough to play, either: the Web Playback SDK
              // has to load and connect afterwards, which is a second or two.
              // So this only signs in and says so; the next press plays.
              if (spotifyCtx.canAuthorizeInPlace) {
                spotifyCtx
                  .authorize()
                  .then(
                    () =>
                      ctx.callSnackbar(
                        'Signed in to Spotify. Press play again once the player is ready.',
                        false,
                      ),
                    ctx.callSnackbar,
                  );
              } else {
                ctx.callSnackbar(
                  'Not signed in to Spotify, so this track cannot be played. ' +
                    'Sign in from the Spotify page.',
                );
              }
              return;
            }
            if (!spotifyCtx.playerId) {
              ctx.callSnackbar('Spotify Player not ready! Please try again later.');
              return;
            }

            playSpotifyTrack();

            break;

          case Source.SoundCloud:
            /**
             * A signed CDN URL from soundCloudApi.resolveStream, played one of
             * two ways (docs/SOUNDCLOUD.md section 3):
             *
             * - Safari and iOS play HLS natively: audioEl.src = manifest, and
             *   background audio and the lock screen keep working. Asked
             *   first, so hls.js is never even downloaded there.
             * - Chrome, Firefox and Edge cannot, so hls.js is loaded on demand
             *   and attached to the same element. Nothing after the redirect
             *   is authenticated, so no xhrSetup.
             *
             * A preview is a plain MP3 and plays anywhere.
             */
            async function attachSoundCloudStream(stream: SoundCloudStream) {
              if (!curAudioEl) {
                return;
              }

              const nativeHls = curAudioEl.canPlayType('application/vnd.apple.mpegurl') !== '';

              if (stream.kind === 'mp3' || nativeHls) {
                curAudioEl.src = stream.url;
                startAudioEl(curAudioEl);

                return;
              }

              // dist/hls.js, not the package root: the root's `module` entry is
              // an .mjs build that webpack 4's nested acorn cannot parse, and
              // the UMD build is the one that was checked (docs/SOUNDCLOUD.md
              // section 7). Loaded on demand so browsers with native HLS never
              // fetch it.
              const { default: HlsCtor } = await import('hls.js/dist/hls.js');

              // The await above is a real gap: the user may have moved on.
              if (halt || !curAudioEl) {
                return;
              }

              if (!HlsCtor.isSupported()) {
                ctx.callSnackbar('This browser cannot play SoundCloud streams.');
                return;
              }

              // startPosition is how hls.js is told where to begin, and it is
              // better than a seek afterwards: it decides which fragment to
              // fetch first, so a restored track does not download the opening
              // minute in order to throw it away.
              const hls = new HlsCtor(
                resumeSeconds > 0 ? { startPosition: resumeSeconds } : undefined,
              );
              hlsRef.current = hls;

              hls.on(HlsCtor.Events.MANIFEST_PARSED, function startPlayback() {
                // A restored track is left where startPosition put it, paused.
                if (halt || !curAudioEl || isRestore) {
                  return;
                }

                playPromise = curAudioEl.play();
                if (playPromise) {
                  playPromise.catch(catchAudioElementPlayErrors);
                }
              });
              hls.on(HlsCtor.Events.ERROR, function reportHlsError(_event, data) {
                if (!data.fatal) {
                  return;
                }

                console.debug('hls.js fatal error', data);

                if (data.type === HlsCtor.ErrorTypes.NETWORK_ERROR) {
                  // The signed manifest lasts minutes and the segments hours,
                  // so a network failure mid-track is a real one, not expiry.
                  ctx.callSnackbar('SoundCloud stream failed. Press play to try again.');
                } else {
                  markTrackMissingOnError();
                }

                hls.destroy();
                if (hlsRef.current === hls) {
                  hlsRef.current = undefined;
                }
              });

              hls.loadSource(stream.url);
              hls.attachMedia(curAudioEl);
            }

            function playSoundCloudTrack() {
              const api = soundCloudCtxRef.current.soundCloudApi;

              if (!api) {
                // Signed in a moment ago; the api object follows on the next
                // render. Same message the Spotify branch gives.
                ctx.callSnackbar('Signed in to SoundCloud. Press play again.', false);
                return;
              }

              // The library record remembers what SoundCloud said the last
              // time the track was fetched. A track played from search is not
              // in the library, and resolveStream copes with undefined.
              api
                .resolveStream(currentTrack.id, soundCloudAccess(track))
                .then(function play(stream) {
                  if (!curAudioEl || halt) {
                    return;
                  }

                  resetTrackMissing();

                  if (stream.access === 'preview') {
                    ctx.callSnackbar(
                      'SoundCloud only offers a 30-second preview of this track.',
                      false,
                    );
                  }

                  doSetPlayerSizeSmall();

                  return attachSoundCloudStream(stream);
                })
                .catch(function reportStreamFailure(err) {
                  if (halt) {
                    return;
                  }

                  // Gone, blocked in this country, or SoundCloud withdrew every
                  // stream: none of these will be different next time.
                  // Everything else - rate limit, sign-in, network - was
                  // already reported by the api wrapper and should be retried,
                  // not remembered.
                  if (
                    err instanceof SoundCloudApiError &&
                    (err.status === 403 || err.status === 404)
                  ) {
                    markTrackMissingOnError(`soundcloud:${err.status}`);
                  }
                });
            }

            if (!soundCloudCtx.accessToken) {
              // Deliberately no allowRedirect here. On iOS authorising means
              // navigating the whole app to SoundCloud, and being thrown out
              // of the app mid-listen is not a fix - the same call the Dropbox
              // branch above makes for the same reason.
              if (soundCloudCtx.canAuthorizeInPlace) {
                soundCloudCtx.authorize().then(playSoundCloudTrack, ctx.callSnackbar);
              } else {
                ctx.callSnackbar(
                  'Not signed in to SoundCloud, so this track cannot be played. ' +
                    'Sign in from the SoundCloud page.',
                );
              }
              break;
            }

            playSoundCloudTrack();

            break;

          case Source.Mixcloud:
            /**
             * Nothing to resolve and nothing to authorise: the track id is
             * the cloudcast key, and the key is what the widget takes. The
             * iframe does the rest - there is no audio to be had from the
             * API (docs/MIXCLOUD.md section 2).
             */
            setMixcloudRequest({
              key: currentTrack.id,
              startSeconds: resumeSeconds > 0 ? resumeSeconds : undefined,
              autoplay: !isRestore,
            });

            doSetPlayerSizeSmall();

            break;

          case Source.Podcast:
            if (curAudioEl) {
              curAudioEl.src = track.id;
              startAudioEl(curAudioEl);

              doSetPlayerSizeSmall();
            }

            break;

          case Source.Null:
            // NullTrack with id Null is used internally to signify absence of track to play
            // other id values signify an erronous track that should be skipped
            if (currentTrack.id !== 'Null') {
              // Counted, so a playlist full of dangling references stops rather
              // than spinning through every one of them.
              skipTrackOnError('null-track');
            }
            break;

          default:
            ctx.callSnackbar('Unable to play, unknown track type!');
        }
      }

      return function cleanup() {
        if (gapiCtx.ytPlayer.current) {
          try {
            gapiCtx.ytPlayer.current.pauseVideo();
          } catch (err) {
            console.error(err);
          }

          setYtIFrameSrcUrl(undefined);
        }

        // This is intended to pause Audio element playback
        // when switching to a youtube track
        //
        // Not `if (playPromise)` any more: a track restored from the last
        // session is cued rather than played, so there is no promise from here
        // even when the user has since pressed play on the element itself -
        // and leaving it running under a YouTube video is two tracks at once.
        if (curAudioEl && (playPromise || !curAudioEl.paused)) {
          curAudioEl.pause();
        }
        if (curAudioEl) {
          curAudioEl.removeEventListener('error', handleOneDriveExpired);
          curAudioEl.removeEventListener('error', handleGapiExpired);
        }

        if (spotifyCtx.player) {
          spotifyCtx.player.removeListener('player_state_changed', handleSpotifyStateChange);
          spotifyCtx.player.pause().catch(console.error);
        }

        if (hlsRef.current) {
          hlsRef.current.destroy();
          hlsRef.current = undefined;
        }

        // Stops the Mixcloud widget. Clearing the request rather than pausing
        // it from here keeps the iframe pointed at the show it had, so coming
        // back to Mixcloud is a load() and not a fresh handshake.
        setMixcloudRequest(undefined);

        halt = true;
      };
    },
    [currentTrack],
  );

  /** Create YouTube player after iframe src url updates */
  useEffect(
    function youtubePlay() {
      /** Update YT track length */
      function updateTrackLength() {
        // Track is null when not playing a library track
        if (
          track.type !== Source.Null &&
          track.length === TrackLength.NOT_READY &&
          gapiCtx.ytPlayer.current
        ) {
          track.length = gapiCtx.ytPlayer.current.getDuration() * 1000;

          // Trigger re-render to display new length. The queue is rebuilt, so
          // where it came from is passed along or it would be forgotten.
          setActivePlaylist(
            activePlaylistValue.tracks,
            currentPlaylistIndex,
            false,
            sourcePlaylistId,
          );
        }
      }

      function handleYtTrackEnd(ev: YT.OnStateChangeEvent) {
        // The video is genuinely playing, so end the failure run. Deliberately
        // here and not in playVideo's onReady callback: onReady fires as soon
        // as the player exists, which is *before* an error like 153 arrives,
        // so resetting there would clear the count just in time to miss it.
        if (ev.data === YT.PlayerState.PLAYING) {
          notePlaybackStarted();
        }

        if (ev.data === YT.PlayerState.ENDED) {
          // call playlist next to handle finding the next track in line
          // and changing to that track

          next();
        }
      }

      /**
       * https://developers.google.com/youtube/iframe_api_reference#Events
       * onError
       *  This event fires if an error occurs in the player. The API will pass an event object to the event listener function. That object's data property will specify an integer that identifies the type of error that occurred. Possible values are:
       *  2 – The request contains an invalid parameter value. For example, this error occurs if you specify a video ID that does not have 11 characters, or if the video ID contains invalid characters, such as exclamation points or asterisks.
       *  5 – The requested content cannot be played in an HTML5 player or another error related to the HTML5 player has occurred.
       *  100 – The video requested was not found. This error occurs when a video has been removed (for any reason) or has been marked as private.
       *  101 – The owner of the requested video does not allow it to be played in embedded players.
       *  150 – This error is the same as 101. It's just a 101 error in disguise!
       * @param ev
       */
      function handleYTVideoError(ev: YT.OnErrorEvent) {
        let message: string;
        /**
         * Whether YouTube is telling us the video itself is gone, as opposed to
         * refusing to play it here and now.
         *
         * Only code 100 qualifies. 101 and 150 mean the owner disallows
         * embedding or the video is blocked in this country: the video exists,
         * plays perfectly well on youtube.com, and may become playable again
         * when the owner changes a setting or the user changes country.
         * Recording those as missing would be a lie the user might act on by
         * deleting the track. 2, 5 and 153 are about the request or the player,
         * not the video at all.
         */
        let videoIsGone = false;

        // Widened deliberately. @types/youtube types ev.data as the union of
        // the five documented codes, and YouTube ships codes that are not in
        // it - 153 being the one that brought us here. Matching only the
        // documented set is how an undocumented code became "error 153" with
        // no handling at all.
        switch (ev.data as number) {
          case 2:
            message = `YouTube responded 'The request contained an invalid parameter value'`;
            break;
          case 5:
            message = `The requested content cannot be played in an HTML5 player.`;
            break;
          case 100:
            message = 'This video has been removed or made private on YouTube.';
            videoIsGone = true;
            break;
          case 101:
            message = `The owner of the requested video does not allow it to be played in embedded players.`;
            break;
          case 150:
            message = 'Video blocked in country';
            break;
          case 153:
            // Undocumented, and it used to fall through to the default and read
            // as a mystery. It means YouTube would not configure the player
            // because the page did not identify itself - see the referrerPolicy
            // on the iframe. It is never about this particular video, so it
            // affects every track in turn, which is what the run counter is for.
            message = 'YouTube would not start the player on this page (error 153).';
            break;
          default:
            message = `YouTube API error ${ev.data}`;
            break;
        }

        // Snackbar only. Reporting each skipped track separately is what turned
        // one misconfigured embed into five Sentry errors; the run report sends
        // the reasons once instead.
        ctx.callSnackbar(message, false);

        // A removed or private video is genuinely gone, so it is worth marking
        // - that is what puts the "!" beside the dead entries a long-lived
        // YouTube playlist collects. Safe to do now only because the flag is no
        // longer believed on its own: usePlaybackFailures retracts it if the
        // next track fails too, so a bad connection cannot mark a whole
        // playlist as removed.
        if (videoIsGone) {
          markTrackMissingOnError(`youtube:${ev.data}`);
        } else {
          skipTrackOnError(`youtube:${ev.data}`);
        }
      }

      if (ytIFrameSrcUrl) {
        gapiCtx.playVideo(updateTrackLength, handleYtTrackEnd, handleYTVideoError, {
          autoplay: ytAutoplayRef.current,
        });
      }
    },
    [ytIFrameSrcUrl],
  );

  // function togglePlayerSize(curSize: PlayerSize) {
  //   if (curSize === PlayerSize.Small) {
  //     ctx.setPlayerSize(PlayerSize.Medium);
  //   } else if (curSize === PlayerSize.Medium) {
  //     ctx.setPlayerSize(PlayerSize.Small);
  //   }
  // }

  /**
   * 'Copy link at current time', which the embedded player has not got: that
   * item exists only on youtube.com, and the iframe is cross-origin, so
   * nothing inside it can be reached from here anyway. Built instead out of
   * the video id we are already holding and the playhead the IFrame API will
   * report.
   *
   * Shift-click copies the plain link, without the timestamp.
   */
  async function copyYouTubeLink(ev: React.MouseEvent<HTMLButtonElement>) {
    const player = gapiCtx.ytPlayer.current;

    // The ref outlives the player it was set from, and a player that has not
    // finished loading has none of these methods yet.
    const seconds =
      !ev.shiftKey && player && typeof player.getCurrentTime === 'function'
        ? Math.floor(player.getCurrentTime())
        : 0;

    const url = `https://youtu.be/${currentTrack.id}` + (seconds > 0 ? `?t=${seconds}` : '');

    try {
      await navigator.clipboard.writeText(url);

      ctx.callSnackbar(
        seconds > 0 ? `Link copied at ${formatDuration(seconds * 1000, true)}` : 'Link copied',
      );
    } catch {
      // The clipboard needs a permission that an insecure origin or an old
      // Safari will not give. Showing the url is the next best thing to
      // handing it over.
      ctx.callSnackbar(`Could not reach the clipboard. The link is ${url}`);
    }
  }

  /**
   * Small device note: Will navigate from full screen now playing view to Main
   */
  function hidePlayer() {
    smallDevSvc.setState(SmallDeviceShow.Main);
    ctx.setPlayerSize(PlayerSize.Hide);
  }
  function showPlayer() {
    if (ctx.settings.lockPlayerSizeMedium) {
      ctx.setPlayerSize(PlayerSize.Medium);
      return;
    }
    if (currentTrack) {
      switch (currentTrack.trackType) {
        case Source.YouTube:
          ctx.setPlayerSize(PlayerSize.Medium);
          break;

        default:
          ctx.setPlayerSize(PlayerSize.Small);
          break;
      }
    } else {
      ctx.setPlayerSize(PlayerSize.Small);
    }
  }

  return (
    <ErrorBoundary>
      {ctx.playerSize === PlayerSize.Hide && screenSize !== ScreenSize.Small && (
        <div className={s.hiddenPlayerControls}>
          <button onClick={showPlayer} className={s.toggleSize}>
            <Open />
          </button>
        </div>
      )}
      <figure
        className={classNames(s.player, s[ctx.playerSize], {
          [s.smallDeviceShow]: smallDevSvc.state === SmallDeviceShow.Player,
        })}
      >
        <section className={s.controls}>
          {/* {track.type === Source.Null &&
            <AddToPlaylist
              playlistItemOrItems={currentTrack}
            />
          } */}

          <button
            className={classNames({
              button: true,
              hollow: ctx.settings.repeatState === RepeatState.None,
              // "": ctx.repeatState === RepeatState.All,
            })}
            onClick={ctx.toggleRepeat}
          >
            {ctx.settings.repeatState === RepeatState.All ? (
              <RepeatAll title="Repeat All" />
            ) : ctx.settings.repeatState === RepeatState.None ? (
              <Repeat title="Repeat None" />
            ) : (
              /*ctx.repeatState === RepeatState.One && */ <Repeat title="Repeat" />
            )}
          </button>

          {currentTrack.trackType === Source.YouTube && (
            <button
              className={classNames('button', s.copyLink)}
              onClick={copyYouTubeLink}
              title="Copy link at current time - shift-click for the plain link"
            >
              <CopyLink />
            </button>
          )}

          <button
            // onClick={() => togglePlayerSize(ctx.playerSize)}
            onClick={hidePlayer}
            className={s.toggleSize}
          >
            {/* {ctx.playerSize === PlayerSize.Small ? '˄' : '˅'} */}
            <Collapse />
          </button>
        </section>

        {track.type !== Source.YouTube && currentTrack.trackType !== Source.Mixcloud && (
          <h4>
            {track.type === Source.Null
              ? ''
              : track.title + (track.artist ? ` - ${track.artist}` : '')}
            {currentTrack.trackType === Source.SoundCloud && (
              // The API terms: SoundCloud's logo, and a visible link back to
              // the track's page, wherever their content plays. Inside the
              // title, not beside it: the player is a fixed 100px, so an
              // extra row of its own pushed the audio element off the bottom.
              <a
                className={s.soundCloudAttribution}
                href={soundCloudPermalink(track) || 'https://soundcloud.com'}
                target="_blank"
                rel="noopener noreferrer"
                title="Open on SoundCloud"
              >
                <img src={soundCloudLogo} alt="SoundCloud" />
              </a>
            )}
          </h4>
        )}

        <div className={s.playbackControlsContainer}>
          <button onClick={prev} className={s.trackNavigation} title="Previous track">
            <Previous />
          </button>

          <iframe
            ref={gapiCtx.playerEl}
            title="youtube-embed"
            className={youTubeEmbedHidden ? 'hide' : ''}
            width="640"
            height="360"
            frameBorder="0"
            allow="autoplay"
            /*
              Required, not cosmetic. Since late 2025 YouTube refuses to
              configure an embedded player that cannot identify its host, and
              it identifies the host by the Referer header. Where the header
              is missing the player fails with error 153 and no video plays.
              iOS is where this bites: a WKWebView does not send the header
              the way a normal page load does, which is why this only ever
              showed up on iPhones.

              strict-origin-when-cross-origin is the value YouTube asks for.
              It is many browsers' default already, and it sends the origin
              alone rather than the full url, so no playlist id leaks.
            */
            referrerPolicy="strict-origin-when-cross-origin"
            src={ytIFrameSrcUrl}
            allowFullScreen
          />

          <audio
            ref={audioEl}
            // if track is not a youtubetrack show the audio element
            className={classNames({
              [s.audio]: true,
              hide:
                currentTrack.trackType === Source.YouTube ||
                currentTrack.trackType === Source.Spotify ||
                currentTrack.trackType === Source.Mixcloud,
            })}
            controls
            // This will remove "credentials: include" from the html audio elements fetch requests
            // That behavior was breaking our calls to at least google's API since they do not return support for that in their CORS response
            crossOrigin="anonymous"
            /*
              Focus never stays on the native controls. In Chromium, once the
              focus is inside them - which is where a click on the timeline
              puts it - every key is theirs: space, ctrl+L, shift+arrow,
              even Tab; nothing reaches a listener on the page, not even one
              in the capture phase on window (measured in Edge, 2026-09-27).
              And space on the timeline toggles nothing, so one seek by mouse
              left every shortcut dead until the next click somewhere else.

              Sending the focus straight back to the body costs the click
              nothing: the seek happens, a drag keeps following the mouse,
              the play button still toggles. What it gives up is tabbing
              into these controls, and each of them has a shortcut.
            */
            onFocus={(ev) => ev.currentTarget.blur()}
          />

          <SpotifyPlayerWrapper
            className={classNames({
              [s.spotifyPlayer]: true,
              hide: currentTrack.trackType !== Source.Spotify,
            })}
            playlistItem={currentTrack}
          />

          <MixcloudPlayerWrapper
            ref={mixcloudPlayer}
            className={classNames({
              [s.mixcloudPlayer]: true,
              hide: currentTrack.trackType !== Source.Mixcloud,
            })}
            hidden={currentTrack.trackType !== Source.Mixcloud}
            request={mixcloudRequest}
            onEnded={next}
            onPlaying={notePlaybackStarted}
            onError={skipTrackOnError}
            onProgress={(positionMs, durationMs) =>
              recordPosition(positionMs, durationMs, Source.Mixcloud)
            }
          />

          <button onClick={next} className={s.trackNavigation} title="Next track">
            <Next />
          </button>
        </div>
      </figure>
    </ErrorBoundary>
  );
}
